import { ISavedPageAddRequest } from "../models/SavedPage/ISavedPageAddRequest";
import { ISavedPageGetResponse } from "../models/SavedPage/ISavedPageGetResponse";

/** Same-origin healio widgets proxy (IIS → Vercel). */
export const HEALIO_WIDGETS_SAVED_API_BASE = "/healiowidgets/api/saved";

export type HealioSavedRow = {
  targetType: string;
  targetId: string;
  title?: string | null;
  url?: string | null;
  data?: string | null;
  savedAt?: string;
};

export type ParsedSavedMeta = {
  site?: string;
  expires?: string;
  teaserHtml?: string;
  imageUrl?: string;
  postedDate?: string;
  engagementText?: string;
  cardKind?: string;
  /** CME activity cards: the top credit type's max credits, "0.25" (see ActivityCardDetail.cshtml). */
  credits?: string;
};

export function parseSavedMeta(data: string | null | undefined): ParsedSavedMeta {
  if (!data || typeof data !== "string") {
    return {};
  }
  try {
    const parsed = JSON.parse(data) as ParsedSavedMeta;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function legacyContentTypeToTargetType(contentType: string): string {
  const normalized = (contentType || "news").trim().toLowerCase();
  if (normalized === "podcastepiside") {
    return "podcastepisode";
  }
  // Healio CME saves under the same type as Vindico and the migrated CME rows
  // ("activity"). The icons and Sitecore still call the content type "cme"; the
  // rename happens only at the saved-items API boundary (save, list, delete).
  if (normalized === "cme") {
    return "activity";
  }
  return normalized || "news";
}

/**
 * Target types saved under two names: the live name and the one the Mongo
 * migration wrote (podcast-episode, news-article, ...). The saved API's `?targetType=`
 * filter now matches both names, but the aliased types are still listed
 * unfiltered here and matched on the canonical name, so a row stored under
 * either name is found whichever name the icon uses.
 */
const ALIASED_TARGET_TYPES = new Set([
  "news",
  "podcastepisode",
  "podcastseries",
  "clinicalguidance",
  "learntheheart",
]);

/** "podcast-episode" -> "podcastepisode", "news-article" -> "news". */
export function canonicalTargetType(targetType: string): string {
  const flat = legacyContentTypeToTargetType(targetType).replace(/[-_]/g, "");
  return flat === "newsarticle" ? "news" : flat;
}

export function isAliasedTargetType(targetType: string): boolean {
  return ALIASED_TARGET_TYPES.has(canonicalTargetType(targetType));
}

export function targetTypeFromAddRequest(
  request: ISavedPageAddRequest
): string {
  const fromArray =
    request.contenttype && request.contenttype.length > 0
      ? request.contenttype[0]
      : "news";
  return legacyContentTypeToTargetType(fromArray);
}

export function buildSavedDataPayload(
  request: ISavedPageAddRequest,
  extras?: Partial<ParsedSavedMeta>
): Record<string, unknown> {
  return {
    site: request.site,
    expires: request.expires ?? "",
    contentTypes: request.contenttype,
    ...extras,
  };
}

export function mapRowToGetResponse(row: HealioSavedRow): ISavedPageGetResponse {
  const meta = parseSavedMeta(row.data ?? undefined);
  return {
    documentid: row.targetId,
    site: meta.site ?? "",
    expires: meta.expires ?? "",
  };
}

export async function fetchSavedForSession(options?: {
  targetType?: string;
  slim?: boolean;
}): Promise<HealioSavedRow[]> {
  const params = new URLSearchParams();
  if (options?.targetType) {
    params.set("targetType", legacyContentTypeToTargetType(options.targetType));
  }
  if (options?.slim) {
    params.set("slim", "1");
  }
  const query = params.toString();
  const url = `${HEALIO_WIDGETS_SAVED_API_BASE}/me${query ? `?${query}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    cache: "no-store",
  });

  if (response.status === 401) {
    return [];
  }
  if (!response.ok) {
    throw new Error(`Saved list failed (${response.status})`);
  }

  const json = (await response.json()) as { items?: HealioSavedRow[] };
  return json.items ?? [];
}

export async function upsertSavedItem(
  request: ISavedPageAddRequest,
  extras?: Partial<ParsedSavedMeta>
): Promise<boolean> {
  const targetType = targetTypeFromAddRequest(request);
  const payload = {
    contactId: request.userid,
    targetType,
    targetId: request.documentid,
    title: request.title,
    url: request.url,
    data: buildSavedDataPayload(request, extras),
  };

  const response = await fetch(HEALIO_WIDGETS_SAVED_API_BASE, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  });

  return response.ok;
}

export async function deleteSavedItem(input: {
  targetType: string;
  targetId: string;
}): Promise<boolean> {
  const params = new URLSearchParams({
    targetType: legacyContentTypeToTargetType(input.targetType),
    targetId: input.targetId,
  });
  const response = await fetch(
    `${HEALIO_WIDGETS_SAVED_API_BASE}?${params.toString()}`,
    {
      method: "DELETE",
      credentials: "same-origin",
      keepalive: true,
    }
  );
  return response.ok;
}
